import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../utils/cn';

export interface CollapsibleMetricsSectionProps {
  /** localStorage key suffix, e.g. "programs" -> "metrics-collapsed-programs" */
  storageKey: string;
  children: React.ReactNode;
  /** Whether the section starts expanded (default: true) */
  defaultExpanded?: boolean;
}

/**
 * CollapsibleMetricsSection - Collapsible wrapper for page KPI/metric cards.
 *
 * Smoothly animates open/close with CSS max-height transitions and persists
 * the collapsed state per-page in localStorage.
 */
export const CollapsibleMetricsSection: React.FC<CollapsibleMetricsSectionProps> = ({
  storageKey,
  children,
  defaultExpanded = true,
}) => {
  const lsKey = `metrics-collapsed-${storageKey}`;

  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(lsKey);
      if (stored !== null) return stored !== 'true'; // stored value is "collapsed"
    } catch {
      // localStorage unavailable
    }
    return defaultExpanded;
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<string>(expanded ? 'none' : '0px');

  // Measure content height for smooth animation
  const measure = useCallback(() => {
    if (contentRef.current) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    }
  }, []);

  useEffect(() => {
    if (expanded) {
      measure();
      // After transition completes, remove max-height constraint so content
      // can grow dynamically (e.g. window resize).
      const timer = setTimeout(() => setMaxHeight('none'), 300);
      return () => clearTimeout(timer);
    }
    // Snap to measured height first so the transition animates from a real value
    if (contentRef.current) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    }
    // Force reflow, then collapse
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setMaxHeight('0px'));
    });
    return undefined;
  }, [expanded, measure]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try {
      if (next) {
        localStorage.removeItem(lsKey);
      } else {
        localStorage.setItem(lsKey, 'true');
      }
    } catch {
      // ignore
    }
  };

  return (
    <div className="mb-6">
      {/* Toggle bar */}
      <button
        type="button"
        onClick={toggle}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium uppercase tracking-wide',
          'text-[var(--muted)] hover:text-[var(--text)] transition-colors select-none',
          'hover:bg-white/5 mb-1'
        )}
      >
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 transition-transform duration-200',
            !expanded && '-rotate-90'
          )}
        />
        <span>Metrics</span>
      </button>

      {/* Collapsible content */}
      <div
        ref={contentRef}
        style={{ maxHeight, opacity: expanded ? 1 : 0 }}
        className="overflow-hidden transition-all duration-300 ease-in-out"
      >
        {children}
      </div>
    </div>
  );
};

CollapsibleMetricsSection.displayName = 'CollapsibleMetricsSection';
